import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import PrescriptionService, {
  Prescription,
} from "../../../../services/prescriptionService";
import PrescriptionModal from "./PrescriptionModal";

interface PrescriptionHistoryProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  onGeneratePDF?: (prescriptionId: number) => void;
  onSendWhatsApp?: (prescriptionId: number) => void;
  refreshTrigger?: number;
  onEdit?: (prescription: Prescription) => void; // NEW
}

const PrescriptionHistory: React.FC<PrescriptionHistoryProps> = ({
  patient,
  isOpen,
  onClose,
  onGeneratePDF,
  onSendWhatsApp,
  refreshTrigger,
}) => {
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPrescription, setSelectedPrescription] =
    useState<Prescription | null>(null);
  const [actionLoading, setActionLoading] = useState<{
    [key: number]: { pdf?: boolean; whatsapp?: boolean };
  }>({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [prescriptionToEdit, setPrescriptionToEdit] =
    useState<Prescription | null>(null);

  useEffect(() => {
    if (isOpen && patient.id) {
      loadPrescriptions();
    }
  }, [isOpen, patient.id, refreshTrigger]);

  const loadPrescriptions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await PrescriptionService.getPatientPrescriptions(
        patient.id
      );
      setPrescriptions(result);
    } catch (err: any) {
      setError(err.message || "Failed to load prescriptions");
    } finally {
      setIsLoading(false);
    }
  };
  const handleEditPrescription = async (prescription: Prescription) => {
    try {
      // Fetch the full prescription details
      const fullPrescription = await PrescriptionService.getPrescription(
        prescription.id!
      );
      setPrescriptionToEdit(fullPrescription);
      setIsEditModalOpen(true);
    } catch (error: any) {
      alert(`Failed to load prescription: ${error.message}`);
    }
  };

  // Add handler for when edit is saved
  const handlePrescriptionUpdated = (prescriptionId: number) => {
    setIsEditModalOpen(false);
    setPrescriptionToEdit(null);
    loadPrescriptions(); // Reload the list
  };
  const handleGeneratePDF = async (prescriptionId: number) => {
    setActionLoading((prev) => ({
      ...prev,
      [prescriptionId]: { ...prev[prescriptionId], pdf: true },
    }));

    try {
      // Updated to use the new print view method (like lab requisitions)
      PrescriptionService.generatePDF(prescriptionId);

      // Update prescription state to mark as PDF generated
      setPrescriptions((prev) =>
        prev.map((p) =>
          p.id === prescriptionId ? { ...p, pdf_generated: true } : p
        )
      );

      if (onGeneratePDF) {
        onGeneratePDF(prescriptionId);
      }
    } catch (err: any) {
      console.error("Generate PDF error:", err);
      alert(`Failed to generate PDF: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [prescriptionId]: { ...prev[prescriptionId], pdf: false },
      }));
    }
  };

  const handleSendWhatsApp = async (prescriptionId: number) => {
    setActionLoading((prev) => ({
      ...prev,
      [prescriptionId]: { ...prev[prescriptionId], whatsapp: true },
    }));

    try {
      const result = await PrescriptionService.sendWhatsApp(prescriptionId);

      // Update prescription state
      setPrescriptions((prev) =>
        prev.map((p) =>
          p.id === prescriptionId
            ? {
                ...p,
                whatsapp_sent: true,
                whatsapp_sent_at: new Date().toISOString(),
              }
            : p
        )
      );

      // Open WhatsApp link automatically
      if (result.whatsapp_url) {
        window.open(result.whatsapp_url, "_blank");
        alert(`WhatsApp opened for ${result.phone}`);
      } else {
        alert(`WhatsApp sent successfully: ${result.message}`);
      }

      if (onSendWhatsApp) {
        onSendWhatsApp(prescriptionId);
      }
    } catch (err: any) {
      alert(`Failed to send WhatsApp: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [prescriptionId]: { ...prev[prescriptionId], whatsapp: false },
      }));
    }
  };

  const handleGenerateAndSend = async (prescriptionId: number) => {
    setActionLoading((prev) => ({
      ...prev,
      [prescriptionId]: { pdf: true, whatsapp: true },
    }));

    try {
      const result = await PrescriptionService.generateAndSendPrescription(
        prescriptionId,
        true
      );

      // Update prescription state
      setPrescriptions((prev) =>
        prev.map((p) =>
          p.id === prescriptionId
            ? {
                ...p,
                pdf_generated: true,
                pdf_path: result.pdf_path,
                whatsapp_sent: result.whatsapp_sent || false,
                whatsapp_sent_at: result.whatsapp_sent
                  ? new Date().toISOString()
                  : p.whatsapp_sent_at,
              }
            : p
        )
      );

      // Open PDF in new tab
      window.open(result.pdf_url, "_blank");

      // Auto-open WhatsApp if generated
      if (result.whatsapp_url) {
        setTimeout(() => {
          window.open(result.whatsapp_url, "_blank");
        }, 1000); // Small delay to let PDF open first
      }

      // alert("Prescription generated and WhatsApp link opened!");
    } catch (err: any) {
      alert(`Failed to generate and send: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({
        ...prev,
        [prescriptionId]: { pdf: false, whatsapp: false },
      }));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "#10b981";
      case "completed":
        return "#6b7280";
      case "cancelled":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  if (!isOpen) return null;

  const containerStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
    height: "auto",
    minHeight: "400px",
    maxHeight: "600px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const headerStyle: React.CSSProperties = {
    padding: "0 0 16px 0",
    borderBottom: "1px solid #e5e7eb",
    marginBottom: "16px",
    flexShrink: 0,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: "18px",
    fontWeight: "600",
    margin: 0,
    marginBottom: "4px",
    lineHeight: "1.3",
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
    wordBreak: "break-word",
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
  };

  const loadingStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
    flexDirection: "column",
    gap: "12px",
  };

  const prescriptionCardStyle: React.CSSProperties = {
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    overflow: "hidden",
    transition: "box-shadow 0.2s ease",
    marginBottom: "16px",
  };

  const prescriptionHeaderStyle: React.CSSProperties = {
    background: "#f8fafc",
    padding: "16px",
    borderBottom: "1px solid #e5e7eb",
  };

  const headerContentStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  };

  const prescriptionTitleStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
    flexWrap: "wrap",
  };

  const infoGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "8px",
    fontSize: "14px",
    color: "#6b7280",
  };

  const actionButtonsStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "12px",
  };

  const mobileActionButtonsStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "12px",
  };

  const prescriptionBodyStyle: React.CSSProperties = {
    padding: "16px",
  };

  const medicinesListStyle: React.CSSProperties = {
    marginBottom: "16px",
  };

  const medicineItemStyle: React.CSSProperties = {
    background: "#f9fafb",
    border: "1px solid #f3f4f6",
    borderRadius: "6px",
    padding: "12px",
    marginBottom: "8px",
  };

  const medicineInfoGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "6px",
    fontSize: "13px",
  };

  const summaryStyle: React.CSSProperties = {
    marginTop: "16px",
    padding: "16px",
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    textAlign: "center",
    fontSize: "14px",
    color: "#6b7280",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "success" | "warning" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#3b82f6", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      success: { background: "#10b981", color: "white" },
      warning: { background: "#f59e0b", color: "white" },
    };

    return {
      padding: "8px 12px",
      borderRadius: "6px",
      border: "none",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      flex: "1",
      minWidth: "100px",
      whiteSpace: "nowrap",
      ...variants[variant],
    };
  };

  // Determine if we're on mobile
  const isMobile = window.innerWidth < 768;

  return (
    <>
      <div style={containerStyle}>
        {/* Header - Fixed at top */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>📋 Prescription History</h2>
          <p style={subtitleStyle}>
            All prescriptions for{" "}
            {patient.full_name || `${patient.first_name} ${patient.last_name}`}
          </p>
        </div>

        {/* Content - Scrollable */}
        <div style={contentStyle}>
          {isLoading ? (
            <div style={loadingStyle}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #e2e8f0",
                  borderTop: "4px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              Loading prescriptions...
            </div>
          ) : error ? (
            <div
              style={{
                textAlign: "center",
                color: "#ef4444",
                padding: "40px",
                fontSize: "16px",
              }}
            >
              ⚠️ {error}
              <br />
              <button
                onClick={loadPrescriptions}
                style={{ ...buttonStyle("primary"), marginTop: "16px" }}
              >
                Retry
              </button>
            </div>
          ) : prescriptions.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                color: "#6b7280",
                padding: "40px",
                fontSize: "16px",
              }}
            >
              📋 No prescriptions found for this patient
            </div>
          ) : (
            <div>
              {/* Prescriptions List */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {prescriptions.map((prescription) => (
                  <div
                    key={prescription.id}
                    style={prescriptionCardStyle}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0, 0, 0, 0.1)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.boxShadow = "none")
                    }
                  >
                    {/* Prescription Header */}
                    <div style={prescriptionHeaderStyle}>
                      <div style={headerContentStyle}>
                        <div style={prescriptionTitleStyle}>
                          <h3
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              margin: 0,
                              color: "#1f2937",
                            }}
                          >
                            Prescription {prescription.id}
                          </h3>
                          <span
                            style={{
                              padding: "4px 8px",
                              borderRadius: "12px",
                              fontSize: "11px",
                              fontWeight: "500",
                              background:
                                prescription.status === "active"
                                  ? "#dcfce7"
                                  : prescription.status === "completed"
                                  ? "#f3f4f6"
                                  : "#fef2f2",
                              color: getStatusColor(prescription.status),
                              textTransform: "capitalize",
                            }}
                          >
                            {prescription.status}
                          </span>
                        </div>

                        <div style={infoGridStyle}>
                          <div>
                            <strong>Date:</strong>{" "}
                            {formatDate(prescription.prescription_date)}
                          </div>
                          <div>
                            <strong>Doctor:</strong> Dr.{" "}
                            {prescription.doctor?.first_name}{" "}
                            {prescription.doctor?.last_name}
                          </div>
                          {prescription.doctor?.specialty && (
                            <div>
                              <strong>Specialty:</strong>{" "}
                              {prescription.doctor.specialty}
                            </div>
                          )}
                          <div>
                            <strong>Medicines:</strong>{" "}
                            {prescription.items.length} item(s)
                          </div>
                        </div>

                        {prescription.diagnosis && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              background: "#eff6ff",
                              border: "1px solid #dbeafe",
                              borderRadius: "6px",
                              fontSize: "14px",
                            }}
                          >
                            <strong>Diagnosis:</strong> {prescription.diagnosis}
                          </div>
                        )}

                        {/* Action Buttons - Responsive Layout */}
                        <div
                          style={
                            isMobile
                              ? mobileActionButtonsStyle
                              : actionButtonsStyle
                          }
                        >
                          <button
                            onClick={() => handleGeneratePDF(prescription.id!)}
                            disabled={actionLoading[prescription.id!]?.pdf}
                            style={{
                              ...buttonStyle(
                                prescription.pdf_generated
                                  ? "success"
                                  : "primary"
                              ),
                              opacity: actionLoading[prescription.id!]?.pdf
                                ? 0.7
                                : 1,
                              cursor: actionLoading[prescription.id!]?.pdf
                                ? "not-allowed"
                                : "pointer",
                            }}
                          >
                            {actionLoading[prescription.id!]?.pdf
                              ? "PDF Generating..."
                              : prescription.pdf_generated
                              ? "View PDF"
                              : "Generate PDF"}
                          </button>
                          <button
                            onClick={() => handleEditPrescription(prescription)}
                            style={{
                              padding: isMobile ? "8px 12px" : "10px 16px",
                              background: "#f59e0b",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: isMobile ? "12px" : "13px",
                              fontWeight: "500",
                              cursor: "pointer",
                              // display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              whiteSpace: "nowrap",
                              transition: "all 0.2s",
                            }}
                            disabled={false}
                          >
                            {isMobile ? "✏️" : "✏️ Edit"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Prescription Details */}
                    <div style={prescriptionBodyStyle}>
                      {/* Medicines List */}
                      <div style={medicinesListStyle}>
                        <h4
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            margin: "0 0 12px 0",
                            color: "#374151",
                          }}
                        >
                          Medicines:
                        </h4>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {prescription.items.map((item, index) => (
                            <div key={index} style={medicineItemStyle}>
                              <div style={medicineInfoGridStyle}>
                                <div
                                  style={{
                                    gridColumn: "1 / -1",
                                    marginBottom: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {/* Medicine Name */}
                                  <div
                                    style={{
                                      fontSize: "15px",
                                      fontWeight: "600",
                                      color: "#1f2937",
                                    }}
                                  >
                                    {PrescriptionService.getMedicineDisplayName(
                                      item.medicine!
                                    )}
                                  </div>

                                  {/* Safety warning only for database medicines */}
                                  {item.medicine &&
                                    PrescriptionService.hasSafetyWarnings(
                                      item.medicine
                                    ) && (
                                      <span
                                        style={{
                                          color: "#dc2626",
                                          fontSize: "12px",
                                        }}
                                      >
                                        ⚠️
                                      </span>
                                    )}
                                </div>

                                {item.dosage && (
                                  <div>
                                    <strong>Dosage:</strong> {item.dosage}
                                  </div>
                                )}

                                {item.frequency && (
                                  <div>
                                    <strong>Frequency:</strong> {item.frequency}
                                    {item.prescriptionHint &&
                                      item.prescriptionHint.language ===
                                        "ar" && (
                                        <span
                                          style={{
                                            marginLeft: "8px",
                                            fontSize: "10px",
                                            color: "#6b7280",
                                          }}
                                        >
                                          (Arabic)
                                        </span>
                                      )}
                                  </div>
                                )}

                                {item.duration && (
                                  <div>
                                    <strong>Duration:</strong>{" "}
                                    {PrescriptionService.getDurationDisplayText(
                                      item.duration
                                    )}
                                  </div>
                                )}

                                {item.before_after_meal && (
                                  <div>
                                    <strong>Timing:</strong>{" "}
                                    {PrescriptionService.getMealTimingDisplayText(
                                      item.before_after_meal
                                    )}
                                  </div>
                                )}
                                {item.quantity && item.quantity > 1 && (
                                  <div>
                                    <strong>Quantity:</strong> {item.quantity}
                                  </div>
                                )}
                              </div>

                              {/* Show medicine details only for database medicines */}
                              {item.medicine && (
                                <div
                                  style={{
                                    marginTop: "8px",
                                    fontSize: "11px",
                                    color: "#9ca3af",
                                  }}
                                >
                                  {PrescriptionService.formatMedicineDetails(
                                    item.medicine
                                  )}
                                </div>
                              )}

                              {item.instructions && (
                                <div
                                  style={{
                                    marginTop: "8px",
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Instructions: {item.instructions}
                                </div>
                              )}

                              {item.doctor_notes && (
                                <div
                                  style={{
                                    marginTop: "4px",
                                    fontSize: "12px",
                                    color: "#dc2626",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Doctor's Note: {item.doctor_notes}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* General Notes */}
                      {prescription.doctor_notes && (
                        <div
                          style={{
                            background: "#fef9e7",
                            border: "1px solid #fde68a",
                            borderRadius: "6px",
                            padding: "12px",
                            marginBottom: "12px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "4px",
                              color: "#92400e",
                            }}
                          >
                            General Notes:
                          </div>
                          <div style={{ fontSize: "13px", color: "#78350f" }}>
                            {prescription.doctor_notes}
                          </div>
                        </div>
                      )}

                      {/* Status Information */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: isMobile ? "column" : "row",
                          justifyContent: "space-between",
                          alignItems: isMobile ? "flex-start" : "center",
                          gap: isMobile ? "8px" : "16px",
                          fontSize: "12px",
                          color: "#6b7280",
                          paddingTop: "12px",
                          borderTop: "1px solid #f3f4f6",
                        }}
                      >
                        <div>
                          Created: {formatDate(prescription.created_at!)}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          {prescription.pdf_generated && (
                            <span style={{ color: "#10b981" }}>
                              PDF Generated
                            </span>
                          )}
                          {prescription.whatsapp_sent && (
                            <span style={{ color: "#10b981" }}>
                              WhatsApp Sent{" "}
                              {prescription.whatsapp_sent_at &&
                                `(${formatDate(
                                  prescription.whatsapp_sent_at
                                )})`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Edit Prescription Modal */}
                {isEditModalOpen && prescriptionToEdit && (
                  <PrescriptionModal
                    patient={patient}
                    isOpen={isEditModalOpen}
                    onClose={() => {
                      setIsEditModalOpen(false);
                      setPrescriptionToEdit(null);
                    }}
                    onPrescriptionUpdated={handlePrescriptionUpdated}
                    editMode={true}
                    prescriptionToEdit={prescriptionToEdit}
                  />
                )}
              </div>

              {/* Summary */}
              <div style={summaryStyle}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "16px",
                  }}
                >
                  <span>Total: {prescriptions.length} prescription(s)</span>
                  <span>
                    Active:{" "}
                    {prescriptions.filter((p) => p.status === "active").length}
                  </span>
                  <span>
                    Completed:{" "}
                    {
                      prescriptions.filter((p) => p.status === "completed")
                        .length
                    }
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>
        {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        @media (max-width: 768px) {
          .medicine-info-grid {
            grid-template-columns: 1fr !important;
          }
          
          .prescription-header {
            flex-direction: column !important;
          }
          
          .action-buttons {
            flex-direction: row !important;
            flex-wrap: wrap;
          }
        }
        
        @media (max-width: 480px) {
          .info-grid {
            grid-template-columns: 1fr !important;
          }
          
          .summary-content {
            flex-direction: column !important;
            gap: 8px !important;
          }
        }
      `}
      </style>
    </>
  );
};

export default PrescriptionHistory;
